import { useEffect, useMemo, useState } from "react";
import { moNum } from "@/lib/moNum";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { buildOrderBOM, buildAvailability } from "@/lib/orderBom";
import { toast } from "sonner";

const num = (v) => Number(v) || 0;

// Edit any MO field (product, pack, design, qty, date, price, customer) from the schedule list.
export default function EditMODialog({ mo, products, open, onOpenChange, onSaved }) {
  const [productId, setProductId] = useState("");
  const [cfgId, setCfgId] = useState("");
  const [designId, setDesignId] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("bottles");
  const [date, setDate] = useState("");
  const [price, setPrice] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !mo) return;
    setProductId(mo.product_id || "");
    setCfgId(mo.config_id || "");
    setDesignId(mo.design_id || "");
    setQty(mo.quantity != null ? String(mo.quantity) : "");
    setUnit(mo.unit || "bottles");
    setDate(mo.production_date || "");
    setPrice(mo.price_per_case != null ? String(mo.price_per_case) : "");
    setCustomerId(mo.customer_id || "");
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, [open, mo]);

  const product = products.find((p) => p.id === productId) || null;
  const designs = useMemo(() => {
    const seen = new Set();
    const out = [];
    (product?.lines || []).forEach((l) => (l.designs || []).forEach((d) => { if (d?.id && !seen.has(d.id)) { seen.add(d.id); out.push(d); } }));
    return out;
  }, [product]);
  const cfgs = product?.pack_configs || [];
  const cfg = cfgs.find((c) => c.id === cfgId) || cfgs[0] || null;
  const casePackSize = num(cfg?.bottles_per_case);
  const bpp = casePackSize && cfg?.cases_per_pallet ? casePackSize * cfg.cases_per_pallet : 0;
  const ppt = num(cfg?.pallets_per_truckload);
  const perUnit = unit === "bottles" ? 1 : unit === "cases" ? casePackSize : unit === "pallets" ? bpp : bpp * ppt;
  const totalBottles = num(qty) * perUnit;

  const onProduct = (v) => {
    const p = products.find((x) => x.id === v);
    setProductId(v);
    setCfgId(p?.pack_configs?.[0]?.id || "");
    setDesignId("");
    setPrice(p?.default_price_per_case != null ? String(p.default_price_per_case) : "");
  };

  const valid = product && casePackSize > 0 && num(qty) > 0 && !!date && (designs.length === 0 || !!designId);

  const save = async () => {
    if (!valid || !mo) return;
    setSaving(true);
    try {
      const { data: updated } = await api.put(`/manufacturing-orders/${mo.id}`, {
        product_id: product.id, case_pack: casePackSize, config_id: cfg?.id || "",
        quantity: num(qty), unit, production_date: date, design_id: designId || null,
        price_per_case: price === "" ? null : Number(price),
        customer_id: customerId, customer_name: customers.find((c) => c.id === customerId)?.company_name || "",
      });
      if (mo.placed) {
        // Re-place to refresh ordered bottles, BOM snapshot & availability (keeps MO number & progress).
        const { data: comps } = await api.get("/components");
        const totalCases = casePackSize ? totalBottles / casePackSize : 0;
        const totalPallets = bpp ? totalBottles / bpp : 0;
        const totalTrucks = ppt ? totalPallets / ppt : 0;
        const bom = buildOrderBOM(product, comps, { cfg, casePackSize, totalCases, totalPallets, totalTrucks, designId });
        const compIndex = Object.fromEntries(comps.map((c) => [c.id, c]));
        let reservations = {};
        try { reservations = (await api.get("/components/reservations")).data.by_key || {}; } catch { /* non-blocking */ }
        await api.post(`/manufacturing-orders/${mo.id}/place`, {
          bottle_size: product.name || "",
          ordered_bottles: totalBottles,
          bom_snapshot: bom,
          availability: buildAvailability(bom, compIndex, reservations),
        });
      }
      toast.success(`${moNum(mo.mwo_number) || "MO"} updated`);
      if (updated?.synced_so_number) toast.info(`${updated.synced_so_number} ship date updated to match`);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="edit-mo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Edit {mo ? moNum(mo.mwo_number) || "Draft MO" : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Select value={productId || undefined} onValueChange={onProduct}>
            <SelectTrigger className="rounded-sm" data-testid="edit-mo-product"><SelectValue placeholder="Select a product" /></SelectTrigger>
            <SelectContent>{products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
          </Select>
          <div className="grid grid-cols-2 gap-1.5">
            {designs.length > 0 ? (
              <Select value={designId || undefined} onValueChange={setDesignId}>
                <SelectTrigger className="rounded-sm text-slate-600" data-testid="edit-mo-design"><SelectValue placeholder="Select a design" /></SelectTrigger>
                <SelectContent>{designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}</SelectContent>
              </Select>
            ) : (
              <Input readOnly value="" placeholder="—" className="rounded-sm bg-slate-50 text-slate-400" />
            )}
            {cfgs.length > 1 ? (
              <Select value={cfg?.id || undefined} onValueChange={setCfgId}>
                <SelectTrigger className="rounded-sm" data-testid="edit-mo-pack"><SelectValue placeholder="Select pack" /></SelectTrigger>
                <SelectContent>{cfgs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label || `${c.bottles_per_case}-pack`}</SelectItem>))}</SelectContent>
              </Select>
            ) : (
              <Input readOnly value={casePackSize ? `${casePackSize}-pack` : ""} placeholder="—" className="rounded-sm bg-slate-50 text-slate-600" />
            )}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Input type="text" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Qty" className="rounded-sm" data-testid="edit-mo-qty" />
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="rounded-sm" data-testid="edit-mo-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bottles">Bottles</SelectItem>
                <SelectItem value="cases">Cases</SelectItem>
                <SelectItem value="pallets">Pallets</SelectItem>
                <SelectItem value="truckloads">Truckloads</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm" data-testid="edit-mo-date" />
          <div className="grid grid-cols-2 gap-1.5">
            <div className="relative">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
              <Input type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Price/Case" className="rounded-sm pl-6" title="Price per case (shown on the Production Order document)" data-testid="edit-mo-price" />
            </div>
            <Select value={customerId || "__none"} onValueChange={(v) => setCustomerId(v === "__none" ? "" : v)}>
              <SelectTrigger className={"rounded-sm" + (customerId ? "" : " text-slate-400")} data-testid="edit-mo-customer"><SelectValue placeholder="Produce for…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">— None —</SelectItem>
                {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {totalBottles > 0 && casePackSize > 0 && (
            <p className="text-[11px] text-slate-500">= {Math.round(totalBottles).toLocaleString()} bottles{bpp ? ` · ${(Math.round((totalBottles / bpp) * 100) / 100).toLocaleString()} pallets` : ""}</p>
          )}
          {mo?.placed && <p className="text-[10px] text-slate-400">Saving refreshes this order's BOM &amp; component availability. MO number and recorded production stay unchanged.</p>}
          <Button disabled={!valid || saving} onClick={save} className="w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="edit-mo-save">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
